import { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
  Search,
  CheckSquare,
  Calendar,
  CalendarDays,
  User,
  Tag,
  Crosshair,
  FileText,
  LayoutGrid,
  Inbox,
  Loader2,
  StickyNote,
  type LucideIcon,
} from 'lucide-react';
import { apiGet } from '../lib/api';
import { getEntityUrl } from '../lib/entityUrls';
import type { EntitySearchResult } from '../lib/entityUrls';

interface RichSearchResult extends EntitySearchResult {
  snippet?: string;
  created_at?: string;
  updated_at?: string;
}

const ENTITY_ICONS: Record<string, LucideIcon> = {
  task: CheckSquare,
  meeting_instance: Calendar,
  meeting_series: CalendarDays,
  person: User,
  tag: Tag,
  scope: Crosshair,
  reference_doc: LayoutGrid,
  quick_capture: Inbox,
  note: StickyNote,
};

const ENTITY_COLORS: Record<string, string> = {
  task: '#6366f1',
  meeting_instance: '#f59e0b',
  meeting_series: '#f59e0b',
  person: '#3b82f6',
  tag: '#a855f7',
  scope: '#ec4899',
  reference_doc: '#22c55e',
  quick_capture: '#64748b',
  note: '#14b8a6',
};

const ENTITY_LABELS: Record<string, string> = {
  task: 'Task',
  meeting_instance: 'Meeting',
  meeting_series: 'Series',
  person: 'Person',
  tag: 'Tag',
  scope: 'Project',
  reference_doc: 'View',
  quick_capture: 'Capture',
  note: 'Note',
};

const TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'task', label: 'Tasks' },
  { value: 'meeting_instance', label: 'Meetings' },
  { value: 'meeting_series', label: 'Meeting Series' },
  { value: 'person', label: 'People' },
  { value: 'tag', label: 'Tags' },
  { value: 'scope', label: 'Projects' },
  { value: 'reference_doc', label: 'Views' },
  { value: 'note', label: 'Notes' },
];

function highlightQuery(text: string, query: string): React.ReactNode {
  if (!query.trim()) return text;
  const parts = text.split(new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
  return parts.map((part, i) =>
    part.toLowerCase() === query.toLowerCase()
      ? <mark key={i} className="bg-accent/20 text-text-primary rounded px-0.5">{part}</mark>
      : part
  );
}

const PAGE_SIZE = 20;

export function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const inputRef = useRef<HTMLInputElement>(null);

  const initialQuery = searchParams.get('q') ?? '';
  const initialType = searchParams.get('type') ?? '';
  const initialFrom = searchParams.get('from') ?? '';
  const initialTo = searchParams.get('to') ?? '';

  const [query, setQuery] = useState(initialQuery);
  const [typeFilter, setTypeFilter] = useState(initialType);
  const [dateFrom, setDateFrom] = useState(initialFrom);
  const [dateTo, setDateTo] = useState(initialTo);
  const [results, setResults] = useState<RichSearchResult[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Auto-focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Search when params change
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!query.trim()) {
      setResults([]);
      setTotal(0);
      setHasSearched(false);
      return;
    }

    debounceRef.current = setTimeout(() => {
      doSearch(0, true);
    }, 300);

    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, typeFilter, dateFrom, dateTo]);

  async function doSearch(offset: number, replace: boolean) {
    setLoading(true);
    setHasSearched(true);
    try {
      const params = new URLSearchParams();
      params.set('q', query.trim());
      params.set('limit', String(PAGE_SIZE));
      params.set('offset', String(offset));
      if (typeFilter) params.set('type', typeFilter);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);

      const data = await apiGet<{ results: RichSearchResult[]; total: number }>(
        `/entity-search/rich?${params}`
      );

      if (replace) {
        setResults(data.results);
      } else {
        setResults(prev => [...prev, ...data.results]);
      }
      setTotal(data.total);

      // Update URL params
      const newParams = new URLSearchParams();
      if (query.trim()) newParams.set('q', query.trim());
      if (typeFilter) newParams.set('type', typeFilter);
      if (dateFrom) newParams.set('from', dateFrom);
      if (dateTo) newParams.set('to', dateTo);
      setSearchParams(newParams, { replace: true });
    } catch (err) {
      console.error('Search failed:', err);
    } finally {
      setLoading(false);
    }
  }

  function handleLoadMore() {
    doSearch(results.length, false);
  }

  return (
    <div className="h-full overflow-auto p-3 sm:p-6">
    <div className="max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold text-text-primary mb-6">Search</h1>

      {/* Search input */}
      <div className="flex items-center gap-3 mb-4 px-4 py-3 bg-bg-card border border-border rounded-lg">
        <Search size={18} className="text-text-muted flex-shrink-0" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search everything..."
          className="flex-1 bg-transparent text-text-primary placeholder:text-text-muted focus:outline-none text-sm"
        />
        {loading && <Loader2 size={18} className="text-accent animate-spin flex-shrink-0" />}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <select
          value={typeFilter}
          onChange={e => setTypeFilter(e.target.value)}
          className="flex-1 sm:flex-none px-2 py-1.5 bg-bg-input border border-border rounded text-sm text-text-primary"
        >
          {TYPE_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <input
          type="date"
          value={dateFrom}
          onChange={e => setDateFrom(e.target.value)}
          placeholder="From"
          className="flex-1 sm:flex-none px-2 py-1.5 bg-bg-input border border-border rounded text-sm text-text-primary"
        />
        <span className="text-text-muted text-sm">to</span>
        <input
          type="date"
          value={dateTo}
          onChange={e => setDateTo(e.target.value)}
          placeholder="To"
          className="flex-1 sm:flex-none px-2 py-1.5 bg-bg-input border border-border rounded text-sm text-text-primary"
        />
        {(typeFilter || dateFrom || dateTo) && (
          <button
            onClick={() => { setTypeFilter(''); setDateFrom(''); setDateTo(''); }}
            className="text-xs text-text-muted hover:text-text-primary"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Results */}
      {!hasSearched ? (
        <div className="text-center py-16 text-text-muted">
          <Search size={48} className="mx-auto mb-4 opacity-50" />
          <p className="text-lg mb-2">Search across all your data</p>
          <p className="text-sm">Tasks, meetings, people, tags, projects, and more.</p>
        </div>
      ) : results.length === 0 && !loading ? (
        <div className="text-center py-16 text-text-muted">
          <p className="text-lg mb-2">No results found</p>
          <p className="text-sm">Try a different query or adjust your filters.</p>
        </div>
      ) : (
        <>
          <p className="text-xs text-text-muted mb-3">
            {total} result{total !== 1 ? 's' : ''} found
          </p>
          <div className="space-y-2">
            {results.map(result => {
              const Icon = ENTITY_ICONS[result.entity_type] ?? FileText;
              const iconColor = ENTITY_COLORS[result.entity_type] ?? '#6366f1';
              return (
                <Link
                  key={`${result.entity_type}-${result.entity_id}`}
                  to={getEntityUrl(result)}
                  className="flex items-start gap-3 px-4 py-3 bg-bg-card border border-border rounded-lg hover:border-border-hover transition-colors group"
                >
                  <Icon
                    size={18}
                    className="flex-shrink-0 mt-0.5"
                    style={{ color: iconColor }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-text-primary group-hover:text-accent transition-colors font-medium truncate">
                        {highlightQuery(result.name, query)}
                      </span>
                      <span
                        className="text-[10px] uppercase px-1.5 py-0.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: iconColor + '15', color: iconColor }}
                      >
                        {ENTITY_LABELS[result.entity_type]}
                      </span>
                    </div>
                    {result.snippet && (
                      <p className="text-xs text-text-muted mt-1 line-clamp-2">
                        {highlightQuery(result.snippet, query)}
                      </p>
                    )}
                    {result.created_at && (
                      <p className="text-[10px] text-text-muted mt-1">
                        Created {formatDate(result.created_at)}
                      </p>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Load more */}
          {results.length < total && (
            <div className="text-center mt-4">
              <button
                onClick={handleLoadMore}
                disabled={loading}
                className="px-4 py-2 text-sm bg-bg-card border border-border rounded-md text-text-secondary hover:bg-bg-hover disabled:opacity-50 transition-colors"
              >
                {loading ? 'Loading...' : `Load more (${total - results.length} remaining)`}
              </button>
            </div>
          )}
        </>
      )}
    </div>
    </div>
  );
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
